import { SURFACE } from '../lib/surface';
import { useState } from 'react';
import { z } from 'zod';
import { useServices } from '../app/services';
import { DemoAuthService } from './authService';
import { DEMO_PERSONAS } from '../data/demo/seed';
import { Button, ErrorNotice, Field } from '../components/ui';
import { Icon } from '../components/Icon';
import { DemoBanner, Wordmark } from '../app/Shell';
import accessGrid from '@antreja/ui/assets/antreja-patroon-dark.svg';
import { ROLE_LABEL } from '../lib/roles';
import { friendlyError, type FriendlyError } from '../lib/errors';

const emailSchema = z.string().trim().email('Vul een geldig e-mailadres in.');

export function AuthLayout({ children }: { children: React.ReactNode }) {
  const { mode } = useServices();
  return (
    <>
    <DemoBanner />
    <div className="auth-page">
      <div className="auth-side">
        <img className="auth-grid" src={accessGrid} alt="" aria-hidden="true" />
        <div className="auth-intro">
          <Wordmark on="dark" width={184} />
          {SURFACE === 'admin' ? (
            <>
              <h1>Antreja admin</h1>
              <p>Beheeromgeving voor medewerkers van Antreja: clubs, voorraad, installateurs en het team.</p>
            </>
          ) : (
            <>
              <h1>Wie mag waar naar binnen.</h1>
              <p>Beheer deuren, groepen en passen van je vereniging. Wijzigingen gaan naar het slot zodra een app in de buurt is.</p>
            </>
          )}
        </div>
        <figure className="auth-photo">
          <img
            src="/brand/antreja-clubhuis-800.webp"
            srcSet="/brand/antreja-clubhuis-800.webp 800w, /brand/antreja-clubhuis-1200.webp 1200w"
            sizes="(max-width: 960px) 0px, 40vw"
            width={800}
            height={533}
            alt="Een trainster opent met haar telefoon de deur van het clubhuis."
            loading="lazy"
          />
        </figure>
        <p className="auth-foot small">
          {mode === 'demo' ? 'Demo-omgeving met fictieve gegevens.' : 'Beveiligd met tweestapsverificatie voor beheerders.'}
        </p>
      </div>
      <main className="auth-main" id="main">
        <div className="auth-card">{children}</div>
      </main>
    </div>
    </>
  );
}

export function LoginPage() {
  const { mode, auth } = useServices();
  const [tab, setTab] = useState<'link' | 'password'>('link');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<FriendlyError | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  // Een verlopen of al gebruikte e-maillink komt terug als #error_code=otp_expired.
  const [linkExpired] = useState(() => typeof window !== 'undefined' && /error_code=otp_expired|error=access_denied/.test(window.location.hash));

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Vul de 6 cijfers uit de e-mail in.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await auth.verifyEmailCode(email.trim(), code);
    } catch (err) {
      setServerError(friendlyError(err, 'Deze code klopt niet of is verlopen. Vraag een nieuwe code aan.'));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setServerError(null);
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError(parsed.error.issues[0]!.message);
      return;
    }
    setBusy(true);
    try {
      if (tab === 'link') {
        await auth.sendMagicLink(parsed.data);
        setSent(true);
      } else {
        await auth.signInWithPassword(parsed.data, password);
      }
    } catch (err) {
      setServerError(friendlyError(err, 'Inloggen is niet gelukt.'));
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'demo') {
    const demo = auth as DemoAuthService;
    return (
      <AuthLayout>
        <h2>Demo openen</h2>
        <p className="muted" style={{ marginBottom: 20 }}>
          Kies een rol. Elke rol ziet alleen wat hij volgens de rechtenmatrix mag zien.
        </p>
        <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {DEMO_PERSONAS.map((p) => (
            <li key={p.profileId}>
              <button className="persona" onClick={() => demo.signInAsPersona(p.profileId)}>
                <Icon name="persoon" size={22} />
                <span className="persona-text">
                  <strong>{ROLE_LABEL[p.role]}</strong>
                  <span>
                    {p.name}. {p.description}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h2>Inloggen</h2>
      <p className="muted">{SURFACE === 'admin' ? 'Voor medewerkers van Antreja.' : 'Voor bestuurders en beheerders van je club.'}</p>
      {linkExpired && !sent && (
        <div className="alert alert-warn" role="status">
          Deze link werkt niet (meer). Dat gebeurt soms als je e-mailprogramma de link al heeft gecontroleerd. Vul hieronder je e-mailadres in; je krijgt een code om in te loggen.
        </div>
      )}
      <div className="tabs" role="tablist" aria-label="Inlogmethode">
        <button role="tab" aria-selected={tab === 'link'} onClick={() => { setTab('link'); setSent(false); setServerError(null); }}>
          Code per e-mail
        </button>
        <button role="tab" aria-selected={tab === 'password'} onClick={() => { setTab('password'); setServerError(null); }}>
          Wachtwoord
        </button>
      </div>
      {sent ? (
        <form className="stack" onSubmit={submitCode} noValidate>
          <div className="alert alert-ok" role="status">
            We hebben een e-mail gestuurd naar <strong>{email}</strong> met een code van 6 cijfers. Vul de code hieronder in.
            <span className="small" style={{ display: 'block', marginTop: 6 }}>
              Geen e-mail binnen een paar minuten? Kijk in je map Spam of Ongewenst, of{' '}
              <button type="button" className="linklike" onClick={() => { setSent(false); setCode(''); }}>
                vraag een nieuwe code aan
              </button>
              .
            </span>
          </div>
          <Field label="Code uit de e-mail" error={error}>
            {(p) => (
              <input
                {...p}
                className="input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            )}
          </Field>
          {serverError && <ErrorNotice error={serverError} />}
          <Button variant="primary" type="submit" disabled={busy}>
            {busy ? 'Inloggen…' : 'Inloggen'}
          </Button>
        </form>
      ) : (
        <form className="stack" onSubmit={submit} noValidate>
          <Field label="E-mailadres" error={tab === 'link' ? error : null}>
            {(p) => (
              <input {...p} className="input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            )}
          </Field>
          {tab === 'password' && (
            <Field label="Wachtwoord" error={error}>
              {(p) => (
                <input
                  {...p}
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
          )}
          {serverError && <ErrorNotice error={serverError} />}
          <Button variant="primary" type="submit" disabled={busy}>
            {busy ? (tab === 'link' ? 'Versturen…' : 'Inloggen…') : tab === 'link' ? 'Stuur code' : 'Inloggen'}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
